<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input, Checkbox } from 'ant-design-vue'
  import { PlusOutlined, SearchOutlined, WarningFilled } from '@ant-design/icons-vue'
  import { PlotSchema } from '../models/PlotSchema'

  const props = defineProps<{
    visible: boolean
    plotSchemas: Array<PlotSchema>
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'plot-selected': [plot: PlotSchema]
  }>()

  const availablePlots = ref<PlotSchema[]>([])
  const filteredPlots = ref<PlotSchema[]>([])
  const showHiddenPlots = ref<boolean>(false)
  const loadingPlots = ref<boolean>(false)
  const hasErrorLoadingPlots = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group plots by category
  const groupedPlots = ref<Record<string, PlotSchema[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredPlots.value = availablePlots.value.filter((plotSchema: PlotSchema) => {
        return (
          (plotSchema.description && plotSchema.description.toLowerCase().includes(search)) ||
          (plotSchema.display_name && plotSchema.display_name.toLowerCase().includes(search))
        )
      })
    } else {
      filteredPlots.value = [...availablePlots.value]
    }
    groupPlotsByCategory()
  }

  const groupPlotsByCategory = () => {
    const grouped: Record<string, PlotSchema[]> = {}
    filteredPlots.value.forEach((plot) => {
      if (!grouped[plot.category]) {
        grouped[plot.category] = []
      }
      grouped[plot.category].push(plot)
    })
    groupedPlots.value = grouped
  }

  const selectPlot = (plot: PlotSchema) => {
    emit('plot-selected', plot)
    // Keep dialog open to allow adding multiple plots easily
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailablePlots()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailablePlots()
      nextTick(() => {
        // reset the search model (don't overwrite the Input ref)
        searchText.value = ''

        // try to call component focus() first
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }

        // fallback: find the native input element and focus it
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailablePlots = () => {
    availablePlots.value = props.plotSchemas
    filteredPlots.value = props.plotSchemas
    groupPlotsByCategory()
  }

  // Watch for visibility changes to load plots if needed
  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.plotSchemas,
    (newItems) => {
      availablePlots.value = [...newItems]
      filteredPlots.value = [...newItems]
      groupPlotsByCategory()
    }
  )

  defineExpose({
    availablePlots
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Plot"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="plot-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search plots by name or description..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>
        <Checkbox class="ml-2 flex-shrink-0 whitespace-nowrap" v-model:checked="showHiddenPlots">
          Show All Plots
        </Checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="loadingPlots" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available plots...</p>
      </div>

      <!-- Plots List -->
      <div v-else id="plotsListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingPlots"
          v-for="(plots, category) in groupedPlots"
          :key="category"
          class="mb-6"
        >
          <!-- Only show category if it has plots (after filtering) -->
          <template
            v-if="plots.filter((x) => (showHiddenPlots ? x : x.is_visible === true)).length > 0"
          >
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ category }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ plots.length }} {{ plots.length === 1 ? 'plot' : 'plots' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="plot in plots.filter((x) => (showHiddenPlots ? x : x.is_visible === true))"
                :key="plot.type"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectPlot(plot)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">{{ plot.display_name }}</h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">{{ plot.description }}</p>
                      <div class="flex items-center text-xs">
                        <span
                          class="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {{ plot.fields.length }} field{{ plot.fields.length !== 1 ? 's' : '' }}
                        </span>
                      </div>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingPlots"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading plots.
        </div>
        <!-- No Results -->
        <div v-if="!hasErrorLoadingPlots && filteredPlots.length === 0" class="py-8 text-center">
          <p>
            {{ searchText.trim() ? 'No plots found matching your search.' : 'No plots available.' }}
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs text-gray-400">
            Try adjusting your search terms or clear the search to browse all plots.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">Click on any plot above to add it to your plotting pipeline</div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .plot-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  #plotsListContainer {
    flex: 1;
  }
  #plotsListContainer::-webkit-scrollbar {
    width: 6px;
  }
  #plotsListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  #plotsListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  #plotsListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
